import React, { createContext, useContext, useMemo } from 'react';

import APPLICANT_TYPES from 'constants/applicantTypes';
import useHouseholdMembersQuery from 'hooks/queries/useHouseholdMembersQuery';
import useUserProfileQuery from 'hooks/queries/useUserProfileQuery';
import {
  getFormattedNames,
  isPrimaryMember as getIsPrimaryMember,
  sortMembers,
} from 'pages/AffordaHousehold/utils';

export const HouseholdMembersContext = createContext(null);
export const useHouseholdMembersContext = () => {
  const context = useContext(HouseholdMembersContext);
  if (!context) {
    throw new Error(
      'useHouseholdMembersContext must be used within a HouseholdMembersProvider',
    );
  }

  return context;
};

export const HouseholdMembersProvider = ({ children }) => {
  const { data: householdMembers } = useHouseholdMembersQuery();
  const { data: userProfile } = useUserProfileQuery();

  const value = useMemo(() => {
    const isLoggedPrimaryUser = userProfile?.isPrimaryProspect ?? null;
    const isLoggedSecondaryUser = !isLoggedPrimaryUser;

    const values = {
      adultHouseholdMembers: [],
      filteredHumanHouseholdMembers: [],
      filteredAdultHouseholdMembers: [],
      filteredMembersWithPendingQualifications: [],
      filteredAdultHouseholdMemberNames: [],
      filteredAdultHouseholdMemberNamesAsString: '',
      secondaryAdultHouseholdMembers: [],
      minorHouseholdMembers: [],
      petHouseholdMembers: [],
      primaryHouseholdMember: {},
      currentHouseholdMember: {},
    };

    const sortedHouseholdMembers = sortMembers(householdMembers ?? []);

    const primaryHouseholdMember =
      sortedHouseholdMembers?.find(({ isProspect }) => {
        return isProspect;
      }) ?? {};

    values.primaryHouseholdMember = primaryHouseholdMember;

    const hasInvitedOtherAdults =
      primaryHouseholdMember.invitedOtherAdults ?? false;

    const currentUserProfileMember = sortedHouseholdMembers.find(
      ({ applicantId }) => applicantId === userProfile?.applicantId,
    );

    sortedHouseholdMembers.forEach((householdMember) => {
      const { applicantId, applicantType } = householdMember;
      const isMinor = applicantType === APPLICANT_TYPES.MINOR;
      const isPet = applicantType === APPLICANT_TYPES.PET;
      const isAdult = !isMinor && !isPet;
      const isPrimaryMember = getIsPrimaryMember(householdMember);
      const isCurrentHouseholdMember = applicantId === userProfile?.applicantId;
      const isSecondaryAdult = isAdult && !isPrimaryMember;
      const isLiveInCaretaker =
        applicantType === APPLICANT_TYPES.LIVE_IN_CARETAKER;

      // Skip live in caretakers for now
      if (isLiveInCaretaker) return;

      if (isAdult) {
        values.adultHouseholdMembers.push(householdMember);

        if (hasInvitedOtherAdults) {
          if (
            householdMember.applicantId === currentUserProfileMember.applicantId
          )
            values.filteredAdultHouseholdMembers.push(householdMember);
        } else {
          // if other users were invited, only add the primaryUser
          values.filteredAdultHouseholdMembers.push(householdMember);
        }
      }

      if (!isPet) {
        if (hasInvitedOtherAdults) {
          if (isLoggedPrimaryUser) {
            if (isPrimaryMember || isMinor)
              values.filteredHumanHouseholdMembers.push(householdMember);
          } else {
            if (!isPrimaryMember && isSecondaryAdult)
              values.filteredHumanHouseholdMembers.push(householdMember);
          }
        } else {
          values.filteredHumanHouseholdMembers.push(householdMember);
        }
      }

      if (isSecondaryAdult)
        values.secondaryAdultHouseholdMembers.push(householdMember);
      if (isCurrentHouseholdMember)
        values.currentHouseholdMember = householdMember;
      if (isMinor) values.minorHouseholdMembers.push(householdMember);
      if (isPet) values.petHouseholdMembers.push(householdMember);
    });

    values.filteredAdultHouseholdMemberNames = getFormattedNames(
      values.filteredAdultHouseholdMembers,
    ).map(({ label }) => label);

    const membersCheckQualifications = isLoggedPrimaryUser
      ? values.adultHouseholdMembers?.filter(
          ({ qualifications }) =>
            !qualifications?.completedAssetForm ||
            !qualifications?.completedIncomeForm,
        )
      : values.filteredAdultHouseholdMembers?.filter(
          ({ qualifications }) =>
            !qualifications?.completedAssetForm ||
            !qualifications?.completedIncomeForm,
        );

    // Check the minors, check every minor if legallyResponsible field array has the applicantId matching in membersCheckQualifications
    values.filteredMembersWithPendingQualifications =
      !!membersCheckQualifications?.length
        ? [
            ...membersCheckQualifications,
            ...values.minorHouseholdMembers.filter((minor) => {
              const { legallyResponsible } = minor;
              return legallyResponsible?.some((responsible) =>
                membersCheckQualifications.some(
                  ({ applicantId }) => applicantId === responsible,
                ),
              );
            }),
          ]
        : [];

    return {
      ...values,
      hasData: householdMembers?.length > 0,
      householdMembers: sortedHouseholdMembers,
      isPrimaryUser: isLoggedPrimaryUser,
      isSecondaryUser: isLoggedSecondaryUser,
      isSingleAdultHousehold: values.adultHouseholdMembers.length === 1,
      hasInvitedOtherAdults,
      hasMoreThanTwoAdultMembers: values.adultHouseholdMembers.length > 2,
      isUnInvitedSecondaryUser: isLoggedSecondaryUser && !hasInvitedOtherAdults,
    };
  }, [householdMembers, userProfile]);

  return (
    <HouseholdMembersContext.Provider value={value}>
      {children}
    </HouseholdMembersContext.Provider>
  );
};
